import { useMemo, useState } from "react";
import { comments, poll, type CommentItem } from "@/mocks/gossip";

const roleColor: Record<string, string> = {
  围观看客: "bg-[rgb(var(--paper-deep))] text-[rgb(var(--ink-soft))]",
  十年老粉: "bg-[rgb(204,38,39)]/10 text-[rgb(var(--brand))]",
  鉴瓜大师: "bg-[rgb(224,124,45)]/12 text-[rgb(224,124,45)]",
  专业搬运: "bg-[rgb(126,158,106)]/12 text-[rgb(106,134,88)]",
};

function CommentCard({
  c,
  liked,
  onLike,
}: {
  c: CommentItem;
  liked: boolean;
  onLike: () => void;
}) {
  return (
    <article className="rounded-lg border border-[rgb(var(--ink))]/10 bg-white p-4">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--ink))] text-[15px] font-bold text-[rgb(var(--paper))]">
          {c.user.slice(0, 1)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-[14px] font-bold">{c.user}</h4>
            <span
              className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium ${roleColor[c.role]}`}
            >
              {c.role}
            </span>
          </div>
          <p className="text-[11px] text-[rgb(var(--ink-soft))]">刚刚 · 来自围观现场</p>
        </div>
      </div>

      <p className="mt-3 text-[14px] leading-relaxed text-[rgb(var(--ink))]/85">{c.text}</p>

      <div className="mt-3 flex items-center gap-4 text-[12px] text-[rgb(var(--ink-soft))]">
        <button
          type="button"
          onClick={onLike}
          className={`flex items-center gap-1.5 transition-colors ${
            liked ? "text-[rgb(var(--brand))]" : "hover:text-[rgb(var(--brand))]"
          }`}
        >
          <i className={`${liked ? "ri-heart-3-fill" : "ri-heart-3-line"} h-4 w-4 flex items-center justify-center`}></i>
          <span>{c.likes.toLocaleString()}</span>
        </button>
        <button
          type="button"
          className="flex items-center gap-1.5 hover:text-[rgb(var(--brand))]"
        >
          <i className="ri-chat-3-line h-4 w-4 flex items-center justify-center"></i>
          {c.replies.length}
        </button>
        <button
          type="button"
          className="flex items-center gap-1.5 hover:text-[rgb(var(--brand))]"
        >
          <i className="ri-share-forward-line h-4 w-4 flex items-center justify-center"></i>
          分享
        </button>
        <span className="ml-auto hidden items-center gap-1 text-[11px] sm:flex">
          <i className="ri-flag-line h-3.5 w-3.5 flex items-center justify-center"></i>
          该评论仅代表网友观点
        </span>
      </div>

      {c.replies.length > 0 && (
        <div className="mt-3 flex flex-col gap-2 rounded-lg bg-[rgb(var(--paper))] p-3">
          {c.replies.map((r, i) => (
            <div key={i} className="flex items-start gap-2 text-[13px]">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--paper-deep))] text-[11px] font-bold">
                {r.user.slice(0, 1)}
              </span>
              <p className="leading-relaxed text-[rgb(var(--ink))]/80">
                <span className="font-semibold">{r.user}：</span>
                {r.text}
                <span className="ml-1.5 whitespace-nowrap text-[11px] text-[rgb(var(--ink-soft))]">
                  · {r.likes} 赞
                </span>
              </p>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

export default function Comments() {
  const [liked, setLiked] = useState<number[]>([]);
  const [choice, setChoice] = useState<number | null>(null);

  const toggleLike = (id: number) =>
    setLiked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const totalVotes = useMemo(() => poll.options.reduce((s, o) => s + o.votes, 0), []);

  return (
    <section id="comments" className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
      <div className="mb-6 flex flex-col justify-between gap-3 md:mb-8 md:flex-row md:items-end">
        <div>
          <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold tracking-[0.25em] text-[rgb(var(--brand))]">
            <i className="ri-chat-smile-2-line h-4 w-4 flex items-center justify-center"></i>
            围观氛围
          </div>
          <h2 className="font-display text-[24px] font-black leading-tight md:text-[30px]">
            网友现场，热评扎堆
          </h2>
          <p className="mt-1 text-[13px] text-[rgb(var(--ink-soft))]">
            万人蹲后续，评论区比正片还精彩
          </p>
        </div>
        <span className="flex shrink-0 items-center gap-2 self-start rounded-full border border-[rgb(var(--ink))]/10 bg-white px-3 py-1.5 text-[13px] text-[rgb(var(--ink-soft))] md:self-auto">
          <i className="ri-eye-line h-4 w-4 flex items-center justify-center text-[rgb(var(--brand))]"></i>
          12.6 万人在围观
        </span>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* 评论列表 */}
        <div className="flex flex-col gap-4 lg:col-span-2">
          {comments.map((c) => (
            <CommentCard
              key={c.id}
              c={c}
              liked={liked.includes(c.id)}
              onLike={() => toggleLike(c.id)}
            />
          ))}
          <button
            type="button"
            className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-dashed border-[rgb(var(--ink))]/20 bg-white py-3 text-[13px] text-[rgb(var(--ink-soft))] transition-colors hover:border-[rgb(var(--brand))]/40 hover:text-[rgb(var(--brand))]"
          >
            <i className="ri-arrow-down-line h-4 w-4 flex items-center justify-center"></i>
            加载更多热评
          </button>
        </div>

        {/* 右栏：投票 + 发帖入口 */}
        <div className="flex flex-col gap-4">
          {/* 围观投票 */}
          <div className="rounded-lg border border-[rgb(var(--brand))]/20 bg-white p-5">
            <div className="mb-1 flex items-center justify-between">
              <span className="flex items-center gap-2 text-[12px] font-semibold tracking-[0.15em] text-[rgb(var(--brand))]">
                <i className="ri-bar-chart-grouped-line h-4 w-4 flex items-center justify-center"></i>
                瓜友快投
              </span>
              <span className="text-[11px] text-[rgb(var(--ink-soft))]">已投 {totalVotes.toLocaleString()}</span>
            </div>
            <h3 className="font-display mb-1 text-[16px] font-bold">{poll.question}</h3>
            <p className="mb-4 text-[11px] text-[rgb(var(--ink-soft))]">{poll.hint}</p>

            <div className="flex flex-col gap-2.5">
              {poll.options.map((o, i) => {
                const isChosen = choice === i;
                const pct = Math.round((o.votes / totalVotes) * 100);
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setChoice(i)}
                    className={`relative overflow-hidden rounded-md border px-3 py-2.5 text-left transition-colors ${
                      isChosen
                        ? "border-[rgb(var(--brand))]"
                        : "border-[rgb(var(--ink))]/12 hover:border-[rgb(var(--ink))]/30"
                    }`}
                  >
                    {choice !== null && (
                      <span
                        className={`absolute inset-y-0 left-0 ${
                          isChosen ? "bg-[rgb(var(--brand))]/12" : "bg-[rgb(var(--paper-deep))]"
                        }`}
                        style={{ width: `${pct}%` }}
                      ></span>
                    )}
                    <span className="relative flex items-center justify-between gap-2">
                      <span className="truncate text-[13px] font-medium text-[rgb(var(--ink))]/85">
                        {o.label}
                      </span>
                      <span className="whitespace-nowrap text-[12px] font-semibold text-[rgb(var(--ink-soft))]">
                        {choice !== null ? `${pct}%` : ""}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            {choice !== null && (
              <p className="mt-3 flex items-center gap-1.5 text-[12px] text-[rgb(var(--brand))]">
                <i className="ri-checkbox-circle-fill h-4 w-4 flex items-center justify-center"></i>
                你已投出，围观不负责准不准
              </p>
            )}
          </div>

          {/* 发帖入口 */}
          <div className="overflow-hidden rounded-lg bg-[rgb(var(--ink))] p-5 text-[rgb(var(--paper))]">
            <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold tracking-[0.2em] text-[rgb(244,146,36)]">
              <i className="ri-edit-2-line h-4 w-4 flex items-center justify-center"></i>
              一起吃瓜
            </div>
            <p className="mb-4 text-[13px] leading-relaxed text-[rgb(var(--paper))]/75">
              你也蹲到了新瓜？来现场发一条，带上截图或来源，帮更多猹吃明白。
            </p>
            <button
              type="button"
              className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[rgb(var(--brand))] py-2.5 text-[13px] font-semibold text-[rgb(var(--paper))] transition-colors hover:bg-[rgb(var(--brand-deep))]"
            >
              <i className="ri-quill-pen-line h-4 w-4 flex items-center justify-center"></i>
              写一条新瓜
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}